import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { track } from '../lib/analytics';

/**
 * HeroQuoteWidget — Compact instant-quote form embedded inside the Home hero.
 * Captures pickup, dropoff, date, vehicle preference and forwards to /contact
 * with query params so the Contact form can prefill.
 */
export const HeroQuoteWidget = () => {
  const navigate = useNavigate();
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [date, setDate] = useState('');
  const [vehicle, setVehicle] = useState('');

  const todayStr = new Date().toISOString().split('T')[0];

  const handleSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (pickup) params.set('pickup', pickup);
    if (dropoff) params.set('dropoff', dropoff);
    if (date) params.set('date', date);
    if (vehicle) params.set('vehicle', vehicle);
    track('hero_quote_submit', {
      has_pickup: !!pickup,
      has_dropoff: !!dropoff,
      has_date: !!date,
      vehicle: vehicle || 'any',
    });
    navigate(`/contact?${params.toString()}#booking-form`);
  };

  const fieldStyle = {
    width: '100%',
    backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(194,168,120,0.18)',
    color: '#F5F5F5',
    fontSize: '14px',
    fontWeight: 300,
    padding: '12px 14px',
    outline: 'none',
    transition: 'border-color 200ms ease, background-color 200ms ease',
    fontFamily: 'inherit',
    borderRadius: '2px',
  };

  const labelStyle = {
    fontSize: '10px',
    color: 'rgba(194,168,120,0.75)',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    fontWeight: 400,
    marginBottom: '8px',
    display: 'block',
  };

  return (
    <form
      data-testid="hero-quote-widget"
      onSubmit={handleSubmit}
      className="hero-quote-widget"
      style={{
        marginTop: '40px',
        padding: '24px 26px 22px',
        backgroundColor: 'rgba(13,13,13,0.62)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        border: '1px solid rgba(194,168,120,0.18)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(194,168,120,0.04)',
        maxWidth: '640px',
        animation: 'fadeInUp 0.8s cubic-bezier(0.22,1,0.36,1) 0.85s both',
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '18px',
        gap: '12px',
      }}>
        <p style={{
          fontSize: '10px',
          color: 'rgba(194,168,120,0.85)',
          letterSpacing: '3px',
          textTransform: 'uppercase',
          fontWeight: 400,
        }}>
          Reserve Your Journey
        </p>
        <span style={{
          fontSize: '10px',
          color: 'rgba(220,220,220,0.45)',
          letterSpacing: '1.5px',
          fontWeight: 300,
        }}>
          REPLY · UNDER 1 HOUR
        </span>
      </div>

      <div className="hero-quote-grid">
        <div>
          <label htmlFor="hq-pickup" style={labelStyle}>Pickup</label>
          <input
            id="hq-pickup"
            data-testid="hero-quote-pickup"
            type="text"
            placeholder="e.g. Oakville home"
            value={pickup}
            onChange={(e) => setPickup(e.target.value)}
            style={fieldStyle}
            onFocus={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.5)'; e.target.style.backgroundColor = 'rgba(255,255,255,0.06)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.18)'; e.target.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
          />
        </div>

        <div>
          <label htmlFor="hq-dropoff" style={labelStyle}>Drop-off</label>
          <input
            id="hq-dropoff"
            data-testid="hero-quote-dropoff"
            type="text"
            placeholder="e.g. Pearson YYZ"
            value={dropoff}
            onChange={(e) => setDropoff(e.target.value)}
            style={fieldStyle}
            onFocus={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.5)'; e.target.style.backgroundColor = 'rgba(255,255,255,0.06)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.18)'; e.target.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
          />
        </div>

        <div>
          <label htmlFor="hq-date" style={labelStyle}>Date</label>
          <input
            id="hq-date"
            data-testid="hero-quote-date"
            type="date"
            min={todayStr}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ ...fieldStyle, colorScheme: 'dark' }}
            onFocus={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.5)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.18)'; }}
          />
        </div>

        <div>
          <label htmlFor="hq-vehicle" style={labelStyle}>Vehicle</label>
          <select
            id="hq-vehicle"
            data-testid="hero-quote-vehicle"
            value={vehicle}
            onChange={(e) => setVehicle(e.target.value)}
            style={{ ...fieldStyle, appearance: 'none', cursor: 'pointer' }}
            onFocus={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.5)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.18)'; }}
          >
            <option value="">Any vehicle</option>
            <option value="Cadillac Escalade ESV">Cadillac Escalade ESV</option>
            <option value="Mercedes-Benz S-Class">Mercedes-Benz S-Class</option>
            <option value="Chevrolet Suburban Premier">Chevrolet Suburban Premier</option>
          </select>
        </div>
      </div>

      <button
        type="submit"
        data-testid="hero-quote-submit"
        style={{
          marginTop: '20px',
          width: '100%',
          backgroundColor: '#C2A878',
          color: '#0B0B0B',
          padding: '14px 24px',
          fontSize: '12px',
          fontWeight: 500,
          letterSpacing: '1.4px',
          textTransform: 'uppercase',
          border: 'none',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          transition: 'background-color 300ms ease, box-shadow 300ms ease',
          boxShadow: '0 4px 24px rgba(194,168,120,0.18)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D4BA8A'; e.currentTarget.style.boxShadow = '0 6px 32px rgba(194,168,120,0.32)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C2A878'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(194,168,120,0.18)'; }}
      >
        Request Quote
        <ArrowRight size={14} strokeWidth={1.6} />
      </button>
    </form>
  );
};
